<template>
  <div v-if="isSuperUser">
    <a-row :gutter="16">
      <a-col :span="12">
        <div class="card">
          <div class="card-body">
            <h5 class="mb-1">
              <strong>Participants to be invited to the auction</strong>
            </h5>

            <p class="font-weight-light mb-3">
              Please select participants which will be invited to this auction
            </p>
            <a-spin :spinning="loadingParticipants">
              <div style="">
                <a-table
                  :columns="participantColumns"
                  :data-source="participants"
                  :pagination="{ pageSize: 50 }"
                  :row-key="(record) => record.id"
                  :row-selection="{
                    selectedRowKeys: auction.participantIds,
                    onChange: onParticipantSelectionChanged,
                  }"
                >
                  <span slot="lastLogin" slot-scope="text">
                    <a-tag :color="text === null ? 'volcano' : 'green'">
                      {{ text === null ? 'Logged out' : 'Logged in' }} </a-tag
                    ><br />
                    {{ text === null ? '' : 'Logged in since:' + text }}
                  </span>

                  <span slot="actions" slot-scope="record">
                    <a-popconfirm
                      title="Are you sure you want to delete this participant?"
                      ok-text="Yes"
                      cancel-text="No"
                      @confirm="deleteParticipant(record.id)"
                      ><a
                        ><i
                          class="fe fe-x-square align-middle text-gray-6"
                          title="Delete participant" /></a
                    ></a-popconfirm>
                    <router-link :to="`/participants/edit/${record.id}`">
                      <a
                        ><i
                          class="fe fe-edit align-middle text-gray-6"
                          title="Edit user"
                          style="margin-left: 10px"
                      /></a>
                    </router-link>
                  </span>
                </a-table>
              </div>
            </a-spin>
          </div>
        </div>
      </a-col>
      <a-col :span="12">
        <div class="card">
          <div class="card-body">
            <h5 class="mb-4">
              <strong>Auction details</strong>
            </h5>
            <a-form-model
              ref="auctionForm"
              :model="auction"
              :rules="rules"
              @submit="createAuction"
              :form="form"
              layout="vertical"
            >
              <a-row :gutter="16">
                <a-col :span="12">
                  <a-form-model-item
                    label="Auction flow"
                    ref="auctionFlow"
                    prop="flow"
                  >
                    <a-select v-model="auction.flow">
                      <a-select-option
                        v-for="type of flowTypes"
                        :value="type.code"
                        :key="type.code"
                        >{{ type.title }}</a-select-option
                      >
                    </a-select>
                  </a-form-model-item>
                </a-col>
                <a-col :span="12">
                  <a-form-model-item
                    label="Auction type"
                    ref="auctionType"
                    prop="type"
                  >
                    <a-select v-model="auction.type">
                      <a-select-option
                        v-for="type of auctionTypes"
                        :value="type.code"
                        :key="type.code"
                        >{{ type.title }}</a-select-option
                      >
                    </a-select>
                  </a-form-model-item>
                </a-col>
              </a-row>

              <a-form-model-item label="Auction name" prop="name">
                <a-input
                  v-model="auction.name"
                  placeholder="Type name of the auction"
                />
              </a-form-model-item>

              <a-form-model-item label="Auction short name" prop="shortName">
                <a-input
                  v-model="auction.shortName"
                  placeholder="Type short name of the auction"
                />
              </a-form-model-item>

              <a-form-model-item
                label="Auction start"
                :style="{
                  display: 'inline-block',
                }"
                prop="startDate"
              >
                <a-date-picker
                  v-model="auction.startDate"
                  placeholder="Select date"
                  @change="startDateChanged"
                  valueFormat="YYYY-MM-DD"
                />
              </a-form-model-item>

              <a-form-model-item
                label=" "
                :style="{
                  display: 'inline-block',
                  'margin-left': '8px',
                }"
                prop="startTime"
              >
                <a-time-picker
                  v-model="auction.startTime"
                  format="HH:mm"
                  valueFormat="HH:mm"
                  @change="startTimeChanged"
                />
              </a-form-model-item>
              <a-form-model-item
                class="pull-right"
                :style="{
                  display: 'inline-block',
                }"
                label="Open for (hours)"
                prop="duration"
              >
                <a-time-picker
                  @change="durationChanged"
                  format="HH:mm"
                  valueFormat="HH:mm"
                  v-model="auction.duration"
                />
              </a-form-model-item>

              <a-row :gutter="16">
                <a-col :span="12">
                  <a-form-model-item
                    label="Allocation volume"
                    prop="allocationVolume"
                  >
                    <a-input
                      v-model="auction.allocationVolume"
                      suffix="MWh/h"
                      placeholder="Allocation volume"
                    />
                  </a-form-model-item>
                </a-col>

                <a-col :span="12">
                  <a-form-model-item
                    label="Load type"
                    ref="loadType"
                    prop="load"
                  >
                    <a-select
                      v-model="auction.loadType"
                      @change="loadTypeChanged"
                    >
                      <a-select-option
                        v-for="type of loadTypes"
                        :value="type.code"
                        :key="type.code"
                        >{{ type.title }}</a-select-option
                      >
                    </a-select>
                  </a-form-model-item>
                </a-col>
              </a-row>

              <a-row :gutter="16">
                <a-col :span="12">
                  <a-form-model-item label="Min. price" prop="minPrice">
                    <a-input
                      v-model="auction.minPrice"
                      suffix="EUR/MWh"
                      placeholder="Minimal bid price per MWh"
                    />
                    <span class="text-default"
                      >If no Min. price is set leave the value at 0</span
                    >
                  </a-form-model-item>
                </a-col>
                <a-col :span="12">
                  <a-form-model-item label="Max. price" prop="maxPrice">
                    <a-input
                      v-model="auction.maxPrice"
                      suffix="EUR/MWh"
                      placeholder="Maximal bid price per MWh"
                    />
                    <span class="text-default"
                      >If no Max. price is set leave the value at 0</span
                    >
                  </a-form-model-item>
                </a-col>
              </a-row>

              <a-row :gutter="16">
                <a-col :span="12">
                  <a-form-model-item
                    label="Required collateral"
                    prop="requiredCollateral"
                  >
                    <a-input
                      v-model="auction.requiredCollateral"
                      suffix="EUR/MWh"
                      placeholder="Minimum collateral required"
                    />
                    <span class="text-default"
                      >If no collateral is required leave it on 0</span
                    >
                  </a-form-model-item>
                </a-col>
              </a-row>

              <a-form-model-item label="Auction comment">
                <a-textarea
                  v-model="auction.comment"
                  :rows="4"
                  placeholder="Message visible to the customer on the auction screen"
                />
              </a-form-model-item>

              <a-form-item>
                <a-button
                  class="btn btn-success pull-right"
                  @click="createAuction"
                  >Create auction</a-button
                >
              </a-form-item>

              <a-row :gutter="16">
                <a-col :span="24">
                  <h5 class="mb-4 mt-3">Load profile</h5>
                  <div class="mb-10">
                    <a-table
                      :columns="columns"
                      :data-source="auction.allocationPattern"
                      rowKey="hour"
                    >
                      <span slot="hour" slot-scope="text">
                        {{ (text + '').padStart(2, '0') }} -
                        {{ (text + 1 + '').padStart(2, '0') }}h
                      </span>
                      <span slot="volume" slot-scope="text, record">
                        <a-input
                          :value="record.volume"
                          @change="
                            (event) => changeAllocationAt(record.hour, event)
                          "
                        ></a-input>
                      </span>
                    </a-table>
                  </div>
                </a-col>
              </a-row>
              <a-row :gutter="16">
                <a-col :span="24">
                  <LoadPattern
                    :pattern="this.auction.allocationPattern"
                    :shouldReact="true"
                  ></LoadPattern>
                </a-col>
              </a-row>
            </a-form-model>
          </div>
        </div>
      </a-col>
    </a-row>
  </div>
</template>
<script>
const labelCol = {
  xs: { span: 24 },
  sm: { span: 4 },
}
const wrapperCol = {
  xs: { span: 24 },
  sm: { span: 20 },
}

const flowTypes = [
  {
    title: 'Selling',
    code: 'sell',
  },
  {
    title: 'Purchasing',
    code: 'buy',
  },
]

const loadTypes = [
  {
    title: 'Base load',
    code: 'baseload',
  },
  {
    title: 'Peak load',
    code: 'peakload',
  },
  {
    title: '24H',
    code: '24h',
  },
]

const columns = [
  {
    title: 'Hour',
    dataIndex: 'hour',
    scopedSlots: { customRender: 'hour' },
  },
  {
    title: 'Volume MWh',
    dataIndex: 'volume',
    scopedSlots: { customRender: 'volume' },
  },
]

const participantColumns = [
  {
    title: 'Name (Short name)',
    dataIndex: 'nameShortName',
    key: 'nameShortName',
  },
  {
    title: 'VAT Code',
    dataIndex: 'vatCode',
    key: 'vatCode',
  },
  {
    title: 'Collateral',
    dataIndex: 'collateral',
    key: 'collateral',
    align: 'center',
  },
]

let isNumeric = (str) => {
  if (typeof str != 'string') return false // we only process strings!
  return (
    !isNaN(str) && !isNaN(parseFloat(str)) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
  ) // ...and ensure strings of whitespace fail
}

let validateAlocationAmount = (rule, value, callback) => {
  if (!value) {
    return callback(new Error('Please type allocation amount in MWh/h'))
  }
  if (parseInt(value) != value) {
    return callback(new Error('Allocation amount must be a whole number'))
  }
  if (isNumeric(value)) {
    callback()
  } else {
    callback('Please type valid numeric value!')
  }
}
import Vue from 'vue'
import moment from 'moment'
import {
  AuctionService,
  AuctionTypesService,
  ParticipantService,
} from '@/services/api'
import LoadPattern from '@/components/belen/loadpattern'
import { mapGetters } from 'vuex'

export default {
  components: {
    LoadPattern,
  },
  data: function () {
    return {
      // Participants
      loadingParticipants: true,
      participants: [],
      participantColumns,
      //end
      columns,
      auctionTypes: [],
      flowTypes,
      loadTypes,
      form: this.$form.createForm(this),
      labelCol,
      wrapperCol,
      createdAuction: {},
      auctionCreated: false,
      auction: {
        type: null,
        flow: null,
        startDate: null,
        startTime: null,
        duration: '03:00',
        allocationVolume: 0,
        allocationPattern: [],
        requiredCollateral: 0,
        minPrice: 0,
        maxPrice: 0,
        comment: '',
        participantIds: [],
      },
      rules: {
        flow: [
          {
            required: true,
            message: 'Please select auction flow',
            trigger: 'change',
          },
        ],
        type: [
          {
            required: true,
            message: 'Please select auction type',
            trigger: 'change',
          },
        ],
        name: [
          {
            required: true,
            message: 'Please type auction name',
            trigger: 'change',
          },
        ],
        shortName: [
          {
            required: true,
            message: 'Please type auction short name',
            trigger: 'change',
          },
        ],
        startDate: [
          {
            required: true,
            message: 'Please pick auction start date',
            trigger: 'change',
          },
        ],
        startTime: [
          {
            required: true,
            message: 'Please pick auction start time',
            trigger: 'change',
          },
        ],
        duration: [
          {
            required: true,
            message: 'Please select auction duration',
            trigger: 'change',
          },
        ],
        allocationVolume: [
          {
            required: true,
            // message: 'Please specify allocation volume in MWh/h',
            trigger: 'change',
            validator: validateAlocationAmount,
          },
        ],
      },
    }
  },
  methods: {
    moment,
    validateStartDateAndTime() {
      if (this.auction.startDate == null || this.auction.startTime == null) {
        this.$notification['error']({
          message: 'Please check your data',
          description: 'Auction start and end time must be specified',
        })
        return false
      } else {
        let startDateTime = moment(
          this.auction.startDate + ' ' + this.auction.startTime + ':00',
          'YYYY-MM-DD HH:mm:ss'
        )

        if (startDateTime.isBefore(new moment())) {
          this.$notification['error']({
            message: 'Please check your data',
            description: "Auction can't be scheduled in the past",
          })
          return false
        }
        return true
      }
    },
    changeAllocationAt(hour, event) {
      let index = this.auction.allocationPattern.findIndex(
        (row) => row.hour == hour
      )

      Vue.set(
        this.auction.allocationPattern[index],
        'volume',
        isNaN(parseFloat(event.target.value))
          ? ''
          : parseFloat(event.target.value)
      )
    },
    resetForm() {
      this.$refs.auctionForm.resetFields()
    },
    createAuction(event, fromData) {
      this.$refs.auctionForm.validate((valid) => {
        if (valid) {
          if (!this.validateStartDateAndTime()) {
            return
          }

          AuctionService.save(this.auction)
            .then((response) => {
              if (response.data.data) {
                this.createdAuction = response.data.data
                this.auctionCreated = true
                this.$router.push(`/auctions/${this.createdAuction.id}/live`)
              }
            })
            .catch((error) => {
              console.error('Could not create auction', error)
              this.$notification['error']({
                message: 'Please check your data',
                description: 'Auction cound not be created',
              })
            })
        } else {
          return false
        }
      })
    },
    onParticipantSelectionChanged(selectedRowKeys) {
      this.auction.participantIds = selectedRowKeys
    },
    loadTypeChanged(selectedValue) {
      const loadProfile = []
      for (let i = 0; i < 24; i++) {
        switch (selectedValue) {
          case 'baseload':
            loadProfile.push({
              hour: i,
              volume: parseFloat(this.auction.allocationVolume),
            })
            break
          case '24h':
            loadProfile.push({
              hour: i,
              volume: parseFloat(this.auction.allocationVolume),
            })
            break
          case 'peakload':
            loadProfile.push({
              hour: i,
              volume: parseFloat(this.auction.allocationVolume),
            })
            break
        }

        this.auction.allocationPattern = loadProfile
      }
    },
    startDateChanged(value, dateString) {},
    startTimeChanged(value, dateString) {},
    durationChanged(value, dateString) {},
    onChange(value, dateString) {},
    onOk(value) {},
  },
  computed: {
    ...mapGetters('user', ['user']),
    isSuperUser() {
      return ['admin', 'operator'].includes(this.user.role)
    },
  },
  beforeCreate() {
    this.form = this.$form.createForm(this, {})
  },
  mounted() {
    this.loadingParticipants = true

    AuctionTypesService.get()
      .then((response) => {
        this.auctionTypes = response.data.data.map((type) => {
          return {
            id: type.id,
            code: type.name,
            title: type.name,
          }
        })
      })
      .catch((error) => {
        console.error(error)
      })

    ParticipantService.get().then((response) => {
      this.loadingParticipants = false
      this.participants = response.data.data
      // preselect all participants
      this.auction.participantIds = this.participants.map(
        (participant) => participant.id
      )
    })
    // this.auction.allocationPattern = new Array(24).map((val, index) => {
    //   return {
    //     hour: index,
    //     value: 0,
    //   }
    // })
  },
}
</script>

<style>
[data-kit-theme='default'] .ant-table-thead > tr > th,
[data-kit-theme='default'] .ant-table-tbody > tr > td {
  padding: 4px 8px;
}

.ant-col.ant-form-item-label {
  padding-bottom: 0px;
}
.ant-col.ant-form-item-label label {
  margin-bottom: 0px;
}
</style>
